import React, { useState, useEffect } from 'react';
import { Music, X, ChevronDown } from 'lucide-react';

export default function SetlistModals({
  // Create modal
  showCreateModal,
  onCloseCreate,
  onCreateSetlist,
  // Edit modal
  editingSetlist,
  editingSetlistName,
  onCloseEdit,
  onUpdateName,
  // Song picker
  showSongPicker,
  pickerSetlistId,
  onClosePicker,
  onAddSongToSetlist,
  availableSongs,
  getSortedVersions,
  loading
}) {
  const [newName, setNewName] = useState('');
  const [editName, setEditName] = useState('');
  const [selectedSongForVersion, setSelectedSongForVersion] = useState(null);

  // Sync edit name when modal opens
  useEffect(() => {
    if (editingSetlist) setEditName(editingSetlistName);
  }, [editingSetlist, editingSetlistName]);

  // Reset when create modal opens
  useEffect(() => {
    if (showCreateModal) setNewName('');
  }, [showCreateModal]);

  // Reset when picker closes
  useEffect(() => {
    if (!showSongPicker) setSelectedSongForVersion(null);
  }, [showSongPicker]);

  // ── Create Modal ──────────────────────────────────────────
  if (showCreateModal) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="card-3d border border-surface-border-light rounded-card w-full max-w-md p-6">
          <h3 className="text-xl font-semibold mb-4">New Setlist</h3>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && newName.trim() && onCreateSetlist(newName)}
            placeholder="Enter setlist name..."
            className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand mb-4 rounded-pill"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={() => onCreateSetlist(newName)}
              disabled={loading || !newName.trim()}
              className="flex-1 bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-6 py-3 font-semibold rounded-pill"
            >
              {loading ? 'Creating...' : 'Create Setlist'}
            </button>
            <button
              onClick={() => { onCloseCreate(); setNewName(''); }}
              className="bg-white/10 hover:bg-white/20 transition px-6 py-3 font-semibold rounded-pill"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Edit Modal ────────────────────────────────────────────
  if (editingSetlist) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="card-3d border border-surface-border-light rounded-card w-full max-w-md p-6">
          <h3 className="text-xl font-semibold mb-4">Rename Setlist</h3>
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && editName.trim() && onUpdateName(editName)}
            placeholder="Enter setlist name..."
            className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand mb-4 rounded-pill"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={() => onUpdateName(editName)}
              disabled={loading || !editName.trim()}
              className="flex-1 bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-6 py-3 font-semibold rounded-pill"
            >
              Save
            </button>
            <button
              onClick={() => { onCloseEdit(); setEditName(''); }}
              className="bg-white/10 hover:bg-white/20 transition px-6 py-3 font-semibold rounded-pill"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Song Picker Modal ─────────────────────────────────────
  if (showSongPicker) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="card-3d border border-surface-border-light rounded-card w-full max-w-md max-h-[70vh] flex flex-col">
          <div className="p-4 border-b border-surface-border-light flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {selectedSongForVersion ? 'Select Version' : 'Add Song'}
            </h3>
            <button
              onClick={() => { onClosePicker(); setSelectedSongForVersion(null); }}
              className="text-text-subtle hover:text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto flex-1">
            {selectedSongForVersion ? (
              <>
                <button
                  onClick={() => setSelectedSongForVersion(null)}
                  className="text-sm text-brand hover:underline mb-3 flex items-center gap-1"
                >
                  ← Back to songs
                </button>
                <p className="text-sm text-text-subtle mb-3">
                  Select version for: <span className="text-white font-semibold">{selectedSongForVersion.name}</span>
                </p>
                <div className="space-y-2">
                  {getSortedVersions(selectedSongForVersion).map((version, idx, arr) => {
                    const versionNumber = arr.length - idx;
                    return (
                      <button
                        key={version.id}
                        onClick={() => onAddSongToSetlist(pickerSetlistId, selectedSongForVersion.id, version.id)}
                        disabled={loading}
                        className="w-full border border-surface-border-light hover:bg-white/10 disabled:opacity-disabled transition rounded-card-btn p-3 text-left"
                      >
                        <p className="font-semibold">Version {versionNumber}</p>
                        <p className="text-xs text-text-subtle">
                          {new Date(version.uploadDate).toLocaleDateString()} • {version.fileName || 'audio file'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              availableSongs.length > 0 ? (
                <div className="space-y-2">
                  {availableSongs.map(song => {
                    const versions = getSortedVersions(song);
                    return (
                      <button
                        key={song.id}
                        onClick={() => setSelectedSongForVersion(song)}
                        className="w-full border border-surface-border-light hover:bg-white/10 transition rounded-card-btn p-3 text-left flex items-center gap-3"
                      >
                        <Music className="w-5 h-5 text-brand" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{song.name}</p>
                          <p className="text-xs text-text-subtle">
                            {versions.length} version{versions.length !== 1 ? 's' : ''}
                            {song.tempo && ` • ${song.tempo} BPM`}
                          </p>
                        </div>
                        <ChevronDown className="w-5 h-5 text-text-subtle -rotate-90" />
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Music className="w-12 h-12 text-text-subtle opacity-50 mx-auto mb-3" />
                  <p className="text-text-muted">No songs with audio available</p>
                  <p className="text-text-subtle text-sm mt-1">Upload audio to your songs first</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
